/* eslint-disable no-unused-vars */
import React, { useState, useMemo, useCallback, useEffect } from 'react'
import styles from './styles.module.scss'
import data, { randomData } from './data_dummy'
import TableHeaders from './TableHeaders'
import TableBody from './TableBody'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { usePagination } from './usePagination'
import { useDispatch, useSelector } from 'react-redux'
import { togglePopup } from '../../../Redux/popups/popupSlice'
import { useTranslation } from 'react-i18next'

export default function index({ category }) {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { operations } = useSelector((state) => state.operations)
  const { patients } = useSelector((state) => state.patients)
  const { appointments } = useSelector((state) => state.appointments)
  const [sorter, setSorter] = useState({
    type: 'full_name',
    asc: true
  })
  const [nameInput, setNameInput] = useState('')

  const CONTENT_COUNT = 10

  // PERFORMANCE OPTIMIZATION: Create lookup maps once and reuse
  const { appointmentsMap, operationsMap, categoryPatients } = useMemo(() => {
    if (!patients || !appointments || !operations) {
      return { appointmentsMap: {}, operationsMap: {}, categoryPatients: [] };
    }
    
    // Create lookup objects for O(1) access
    const appointmentsMap = {};
    appointments.forEach(app => {
      if (!appointmentsMap[app.patient_id]) {
        appointmentsMap[app.patient_id] = [];
      }
      appointmentsMap[app.patient_id].push(app);
    });
    
    const operationsMap = {};
    operations.forEach(op => {
      operationsMap[op.id] = op;
    });
    
    // Filter patients by category once
    const categoryPatients = patients.filter((entry) => entry?.category === category);
    
    return { appointmentsMap, operationsMap, categoryPatients };
  }, [patients, appointments, operations, category]);

  // PERFORMANCE OPTIMIZATION: Process data efficiently with early filtering
  const fulldata = useMemo(() => {
    if (!categoryPatients.length) return [];
    
    // Early filtering by search term to reduce processing
    const filteredPatients = nameInput 
      ? categoryPatients.filter(patient => 
          patient.full_name?.toLowerCase().includes(nameInput.toLowerCase())
        )
      : categoryPatients;
    
    // Only process filtered patients (much more efficient)
    return filteredPatients.map((entry) => {
      const appointmentsData = appointmentsMap[entry.id] || [];
      return {
        ...entry,
        operations: entry?.operations?.map((op) => operationsMap[op.operation_id]).filter((el) => el),
        appointments: appointmentsData
      };
    });
  }, [categoryPatients, appointmentsMap, operationsMap, nameInput]);

  // Helper function to get patient creation timestamp based on available data
  const getPatientTimestamp = useCallback((patient) => {
    // Only use createdAt timestamp if available
    if (patient.createdAt) {
      return new Date(patient.createdAt).getTime();
    }
    return 0; // Default timestamp if missing
  }, []);

  // PERFORMANCE OPTIMIZATION: Sort already filtered data
  const sortedData = useMemo(() => {
    if (!fulldata.length) return [];
    
    // Sort the already filtered data (much more efficient)
    return fulldata.sort((a, b) => {
      // First priority: User-defined sorting
      const { type, asc } = sorter;
      let sortOrder = asc ? 1 : -1;
      
      if (type === 'full_name') {
        return a.full_name.localeCompare(b.full_name) * sortOrder;
      }
      if (type === 'operation') {
        return a?.operation?.op_name?.localeCompare(b?.operation?.op_name) * sortOrder;
      }
      if (type === 'appointments') {
        return (a?.appointments?.length - b?.appointments?.length) * sortOrder;
      }
      
      // Second priority: Always sort by newest first
      return getPatientTimestamp(b) - getPatientTimestamp(a);
    });
  }, [fulldata, sorter, getPatientTimestamp]);

  const { paginatedData, currentPage, totalPages, pageNumbers, navigateToPage } = usePagination(sortedData, CONTENT_COUNT)

  const handleGoToPtFile = (patient) => {
    const id = patient.id
    navigate(`patient/${id}`)
  }

  const handleAddingNewPatient = () => {
    dispatch(
      togglePopup({
        pointer: 4,
        data: {
          category
        }
      })
    )
  }

  return (
    <div className={styles.container}>
      <div className={styles.header_page_search}>
        <h2 className={styles.page_title}>{t('pt_list')}</h2>
        <div className={styles.search_container}>
          <div className={styles.search_input_container}>
            <input 
              type='text' 
              placeholder={t('search')} 
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)} 
              className={styles.search_input} 
            />
          </div>
          <button className={styles.add_btn} onClick={handleAddingNewPatient}>
            {t('add_new_patient')}
          </button>
        </div>
      </div>
      <table className={styles.part_table} cellSpacing='0'>
        {/* <caption className={styles.table_header}>Statement Summary</caption> */}
        <thead className={styles.table_thead}>
          <TableHeaders t={t} sorter={sorter} setSorter={setSorter} category={category} />
        </thead>
        <tbody className={styles.table_tbody}>
          {paginatedData?.map((entry) => (
            <TableBody t={t} key={entry?.id} entry={entry} handleGoToPtFile={handleGoToPtFile} />
          ))}
        </tbody>
      </table>
      <div className={styles.bottom_control_section}>
        <ul className={styles.pagination_container}>
          {/* Previous Button */}
          {totalPages > 1 && (
            <li className={styles.page_number}>
              <button 
                className={`${styles.page_btn} ${styles.nav_btn}`} 
                onClick={() => navigateToPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
              >
                ‹ Prev
              </button>
            </li>
          )}
          
          {/* Page Numbers with Smart Grouping */}
          {pageNumbers.length > 1 &&
            pageNumbers.map((pageNumber, index) => {
              if (pageNumber === '...') {
                return (
                  <li key={`dots-${index}`} className={styles.page_number}>
                    <span className={styles.page_dots}>...</span>
                  </li>
                )
              }
              
              return (
                <li key={pageNumber} className={`${styles.page_number} ${pageNumber === currentPage ? styles.active : ''}`}>
                  <button className={styles.page_btn} onClick={() => navigateToPage(pageNumber)}>
                    {pageNumber}
                  </button>
                </li>
              )
            })}
          
          {/* Next Button */}
          {totalPages > 1 && (
            <li className={styles.page_number}>
              <button 
                className={`${styles.page_btn} ${styles.nav_btn}`} 
                onClick={() => navigateToPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
              >
                Next ›
              </button>
            </li>
          )}
        </ul>
        
        {/* Page Info */}
        {totalPages > 1 && (
          <div className={styles.page_info}>
            Page {currentPage} of {totalPages} ({sortedData.length} total items)
          </div>
        )}
        
        <div className={styles.export_btns}>{/* */}</div>
      </div>
    </div>
  )
}
